import React from 'react';
import { Card, Typography, Tag, Empty, Tooltip } from 'antd';
import { StarOutlined, TrophyOutlined } from '@ant-design/icons';
import styled from 'styled-components';

const { Title, Text } = Typography;

const StyledCard = styled(Card)`
  .species-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    gap: 16px;
    margin-top: 16px;
  }
  .species-item {
    border: 1px solid #f0f0f0;
    border-radius: 8px;
    overflow: hidden;
    transition: all 0.3s;
    cursor: pointer;

    &:hover {
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
      transform: translateY(-2px);
    }
  }
  .species-image {
    width: 100%;
    height: 140px;
    object-fit: cover;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 48px;
    color: white;
    position: relative;
  }
  .rarity-badge {
    position: absolute;
    top: 8px;
    right: 8px;
    background: rgba(0, 0, 0, 0.75);
    color: white;
    padding: 4px 8px;
    border-radius: 12px;
    font-size: 18px;
    backdrop-filter: blur(4px);
  }
  .species-content {
    padding: 12px;
  }
  .species-name {
    font-weight: 600;
    font-size: 13px;
    margin-bottom: 4px;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
  .species-scientific {
    font-size: 11px;
    font-style: italic;
    color: #8c8c8c;
    margin-bottom: 8px;
    display: -webkit-box;
    -webkit-line-clamp: 1;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
  .rarity-info {
    margin-bottom: 8px;
  }
  .rarity-message {
    font-size: 11px;
    font-weight: 600;
    margin-bottom: 6px;
  }
  .time-rarity {
    font-size: 10px;
    color: #8c8c8c;
    margin-bottom: 4px;
  }
  .footer-info {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 11px;
    margin-top: 8px;
    padding-top: 8px;
    border-top: 1px solid #f0f0f0;
  }
`;

interface SpeciesData {
  id: string;
  type: 'plant' | 'animal';
  scientific_name: string;
  common_name: string;
  occurrence_count: number;
  user_count: number;
  rarity_score: number;
  rarity_tier?: string;
  rarity_message?: string;
  rarity_color?: string;
  rarity_icon?: string;
  time_rarity?: {
    this_year: number;
    this_month: number;
    messages: {
      year: string;
      month: string;
    };
  };
  percentile?: number;
  image?: string;
}

interface RareSpeciesCardProps {
  species: SpeciesData[];
  type: 'rare' | 'common';
  loading?: boolean;
}

const RareSpeciesCard: React.FC<RareSpeciesCardProps> = ({
  species,
  type,
  loading = false
}) => {
  const title = type === 'rare' ? 'Rarest Species' : 'Most Common Species';
  const icon = type === 'rare' ? <StarOutlined /> : null;

  return (
    <StyledCard
      bordered={false}
      loading={loading}
      title={
        <Title level={4} style={{ margin: 0 }}>
          {icon} {title}
        </Title>
      }
    >
      {species.length === 0 ? (
        <Empty description={`No ${type} species data available`} />
      ) : (
        <div className="species-grid">
          {species.map((item) => (
            <Tooltip
              key={item.id}
              title={
                item.time_rarity ? (
                  <div>
                    <div>{item.time_rarity.messages.year}</div>
                    <div>{item.time_rarity.messages.month}</div>
                  </div>
                ) : null
              }
              placement="top"
            >
              <div className="species-item">
                <div className="species-image">
                  {item.image ? (
                    <img src={item.image} alt={item.common_name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : (
                    <span>{item.type === 'plant' ? '🌿' : '🦎'}</span>
                  )}
                  {item.rarity_icon && (
                    <div className="rarity-badge">
                      {item.rarity_icon}
                    </div>
                  )}
                </div>
                <div className="species-content">
                  <div className="species-name">
                    {item.common_name || item.scientific_name}
                  </div>
                  {item.common_name && item.scientific_name && (
                    <div className="species-scientific">
                      {item.scientific_name}
                    </div>
                  )}
                  <div className="rarity-info">
                    {item.rarity_message && (
                      <div
                        className="rarity-message"
                        style={{ color: item.rarity_color || '#722ed1' }}
                      >
                        {item.rarity_message}
                      </div>
                    )}
                    {item.time_rarity && (
                      <div className="time-rarity">
                        {item.time_rarity.messages.month}
                      </div>
                    )}
                  </div>
                  <div className="footer-info">
                    <Tag color={item.type === 'plant' ? 'green' : 'blue'} style={{ fontSize: 10, padding: '0 4px', margin: 0 }}>
                      {item.type}
                    </Tag>
                    <Text type="secondary" style={{ fontSize: 10 }}>
                      {item.occurrence_count} {item.occurrence_count === 1 ? 'sighting' : 'sightings'}
                    </Text>
                  </div>
                </div>
              </div>
            </Tooltip>
          ))}
        </div>
      )}
    </StyledCard>
  );
};

export default RareSpeciesCard;
